import { saveAs } from 'file-saver';
import { compact, get, groupBy, uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionType } from 'react-table';

import { SEARCH_RESULTS_LIMIT } from '~/common/constants/max_lengths';
import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { getSearchConstants } from '~/common/constants/search';
import { canDownloadSearchResultItem } from '~/components/AnalyzerResultsPage/SearchResultsItem/utils';
import { DetailsPanel } from '~/components/DashboardV2/DashboardV2SearchResults/DetailsPanel/DetailsPanel';
import { getHandlersKeyFromSearchResult } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import { DocumentDetailsPanel } from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { ExportExcel } from '~/components/Shared/ExportExcel/ExportExcel';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { Action } from '~/eds/src/types/Action';
import { TableState } from '~/eds/src/types/Table';
import { TableContextType } from '~/enums/TableContextType';
import { FieldId } from '~/evifields/src/types/FieldId';
import {
  SearchDocumentItem,
  SearchDocumentsResult,
  SearchQuery,
} from '~/features/advanced-search/types';
import { useAddToGroup } from '~/features/groups/hooks/useAddToGroup';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { usePermission } from '~/hooks/usePermission';
import { useSearchColumns } from '~/hooks/useSearchColumns';
import { useSearchNavigation } from '~/hooks/useSearchNavigation';
import { api } from '~/redux/api/createApi';
import { TableViewSet } from '~/redux/api/methods/tableViews';
import { ColumnSortOrder } from '~/types/Columns';
import { SearchFilter } from '~/types/DashboardV2';
import { openInNewTab } from '~/utils/browser';
import { testContainsGhostSearchResult } from '~/utils/document.utils';
import { getMaximumByLimit } from '~/utils/number';
import { HandlerName } from '~/utils/table';

import { testIsAnyDocumentNotMovable } from '../utils';

type Props = {
  currentTableView?: TableViewSet;
  isLoading: boolean;
  data?: SearchDocumentsResult;
  query: SearchQuery;
  searchFilters: Record<FieldId, SearchFilter>;
  resultsCache?: Record<string, SearchDocumentItem>;
  state: {
    page: number;
    pageSize: number;
    sortBy: ColumnSortOrder;
    columnOrder: string[];
    columnWidths: Record<string, number>;
    featuredClauses: string[];
  };
  tableSelection: ReturnType<typeof useTableSelection>;
  refreshSearch: () => void;
  onMove: () => void;
  onCopy: () => void;
  onEdit: () => void;
  onEditClauses: () => void;
  onDelete: () => void;
  onAddToGroup: () => void;
  onSetColumnOrder: (
    updatedColumnOrder: Array<string>,
    actionMeta?: { action: string },
  ) => void;
  onPaginate: ({ pageIndex }: { pageIndex: number }) => void;
  onPageSizeChange: (pageSize: number) => void;
  onLoadView: (tableState: TableState) => void;
  onSaveView: (tableState: TableState) => void;
  handleUpdate: (action: ActionType) => void;
};

export const SearchV3Results = ({
  currentTableView,
  isLoading,
  data,
  query,
  searchFilters,
  resultsCache = {},
  state: { page, pageSize, sortBy, columnOrder, columnWidths, featuredClauses },
  tableSelection,
  refreshSearch,
  onMove,
  onCopy,
  onEdit,
  onEditClauses,
  onDelete,
  onAddToGroup,
  onSetColumnOrder,
  onPaginate,
  onPageSizeChange,
  onLoadView,
  onSaveView,
  handleUpdate,
}: Props) => {
  const client = useClientSubDomain();
  const intl = useIntl();
  const isUnifiedDetailsPanelEnabled = useFlag(
    FlagType.UnifiedDocumentDetailsPanel,
  );
  const [documentDetails, setDocumentDetails] = useState<SearchDocumentItem>();
  const [showExportExcel, setShowExportExcel] = useState(false);
  const { EXPORT_COMPLETED_TEXT, EXPORT_DEFAULT_TEXT, EXPORT_DUPLICATED_TEXT } =
    getSearchConstants(intl);

  const [getDocumentOriginal] =
    api.endpoints.getDocumentOriginal.useLazyQuery();
  const { selectedIds, isAllSelected } = tableSelection;
  const { toast } = useToast();

  const hasExportExcel = useFlag(FlagType.DashboardsExportExcel);
  const hasGroupsEditPermission = usePermission({
    permission: {
      resourceId: 'document_groups',
      resourceType: 'edit',
    },
  });
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const {
    panel: addToGroupPanel,
    open: openAddToGroup,
    addToGroupBulkAction,
    addToGroupRowAction,
    canAddToGroup: canEditEntityGroups,
  } = useAddToGroup('document');
  const canAddToGroup = hasGroupedTicketAndDocs && canEditEntityGroups;

  const showDetailsPanel = (rowData: any) => {
    setDocumentDetails(rowData);
    trackSegment('selectOverlayPanel', {
      name: rowData.id,
    });
  };
  const hideDetailsPanel = () => {
    trackSegment('selectClose', {
      name: documentDetails?.id,
    });
    setDocumentDetails(undefined);
  };

  const saveOriginalDocument = async (document: SearchDocumentItem) => {
    const documentHandler = get(document, 'document_handlers[0]', {});
    const { id, document_name, file_type } = documentHandler;
    if (canDownloadSearchResultItem(document)) {
      try {
        const response = await getDocumentOriginal(id).unwrap();
        if (response) {
          saveAs(response, `${document_name}.${file_type}`);
        }
      } catch {
        toast({
          message: intl.formatMessage(
            {
              id: 'SearchV3Results.downloadErrorMessage',
              defaultMessage:
                'Something went wrong with downloading {documentName}',
              description:
                'Error message when document download fails. Example: documentName = "contract.pdf"',
            },
            { documentName: document_name },
          ),
          status: 'danger',
        });
      }
    } else {
      toast({
        message: intl.formatMessage(
          {
            id: 'SearchV3Results.downloadPermissionMessage',
            defaultMessage:
              "You don't have permissions to download {documentName}",
            description:
              'Warning message when user lacks download permissions. Example: documentName = "contract.pdf"',
          },
          { documentName: document_name },
        ),
        status: 'warning',
      });
    }
  };

  const isDuplicateExport = data?.meta.is_duplicate_export;
  const exportButtonTooltip = isDuplicateExport
    ? `${EXPORT_DUPLICATED_TEXT}  ${EXPORT_COMPLETED_TEXT}`
    : EXPORT_DEFAULT_TEXT;

  const actions = hasExportExcel
    ? [
        {
          icon: 'file-spreadsheet',
          label: EXPORT_DEFAULT_TEXT,
          tooltip: exportButtonTooltip,
          disabled: isDuplicateExport,
          onClick: () => {
            setShowExportExcel(true);
          },
        },
      ]
    : [];

  const testIsAnyNotMovable = (selectedRowIds: string[]) => {
    const selectedRows = selectedRowIds.map((id) => resultsCache[id]);

    return testIsAnyDocumentNotMovable(selectedRows);
  };

  const testContainsGhost = (selectedRowIds: string[]) => {
    const selectedRows = selectedRowIds.map((id) => resultsCache[id]);

    return testContainsGhostSearchResult(selectedRows);
  };

  const getBulkActions = ({ selectedRowIds }: TableState) => {
    const isAnyNotMovable = testIsAnyNotMovable(selectedRowIds);
    const containsGhost = testContainsGhost(selectedIds);

    const bulkActions = compact([
      {
        icon: 'folder',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'SearchV3Results.movePrivilegesError',
              defaultMessage:
                'You do not have privileges to move one or more of the items you have selected.',
              description:
                'Error message when user lacks move privileges for selected items',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.moveLabel',
              defaultMessage: 'Move',
              description: 'Label for move bulk action',
            }),
        onClick: onMove,
        disabled: isAnyNotMovable,
      },
      {
        icon: 'clipboard',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'SearchV3Results.copyPrivilegesError',
              defaultMessage:
                'You do not have privileges to copy one or more of the items you have selected.',
              description:
                'Error message when user lacks copy privileges for selected items',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.copyLabel',
              defaultMessage: 'Copy',
              description: 'Label for copy bulk action',
            }),
        onClick: onCopy,
        disabled: isAnyNotMovable,
      },
      {
        icon: 'edit',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'SearchV3Results.editFieldsPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description:
                'Error message when user lacks edit privileges for selected items',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.editFieldsLabel',
              defaultMessage: 'Edit Fields',
              description: 'Label for edit fields bulk action',
            }),
        onClick: onEdit,
        disabled: isAnyNotMovable,
      },
      {
        icon: 'edit',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'SearchV3Results.editClausesPrivilegesError',
              defaultMessage:
                'You do not have privileges to edit one or more of the items you have selected.',
              description:
                'Error message when user lacks edit privileges for selected items',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.editClausesLabel',
              defaultMessage: 'Edit Clauses',
              description: 'Label for edit clauses bulk action',
            }),
        onClick: onEditClauses,
        disabled: isAnyNotMovable,
      },
      {
        icon: 'trash',
        label: isAnyNotMovable
          ? intl.formatMessage({
              id: 'SearchV3Results.deletePrivilegesError',
              defaultMessage:
                'You do not have privileges to delete one or more of the items you have selected.',
              description:
                'Error message when user lacks delete privileges for selected items',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.deleteLabel',
              defaultMessage: 'Delete',
              description: 'Label for delete bulk action',
            }),
        onClick: onDelete,
        disabled: isAnyNotMovable,
      },
      !hasGroupedTicketAndDocs && {
        icon: 'groups' as const,
        label: containsGhost
          ? intl.formatMessage({
              id: 'SearchV3Results.addToGroupPrivilegesError',
              defaultMessage:
                'You do not have privileges to add one or more of the items you have selected to a group.',
              description:
                'Error message when user lacks privileges to add items to group',
            })
          : intl.formatMessage({
              id: 'SearchV3Results.addToGroupLabel',
              defaultMessage: 'Add to Group',
              description: 'Label for add to group bulk action',
            }),
        onClick: onAddToGroup,
        disabled: containsGhost || !hasGroupsEditPermission,
      },
      canAddToGroup && {
        icon: 'groups' as const,
        ...addToGroupBulkAction(
          selectedRowIds.map((id) => String(resultsCache[id]?.id || id)),
        ),
        disabled: containsGhost,
      },
    ]);

    return bulkActions;
  };

  const getRowActions = canAddToGroup
    ? (item: SearchDocumentItem) => {
        if (!item.permissions?.add_to_group) {
          return [];
        }
        return [
          {
            ...addToGroupRowAction,
            onClick: () =>
              openAddToGroup([
                { resourceType: 'document', resourceId: String(item.id || '') },
              ]),
          },
        ];
      }
    : undefined;

  const onOpenDocumentClick = () => {
    if (documentDetails) {
      if (documentDetails.document_handlers.length > 1) {
        //@ts-ignore callback type not supported in js hook
        setDocumentSelected(documentDetails);
        //@ts-ignore callback type not supported in js hook
        setOpenDocumentInNewTab(true);
      } else {
        openInNewTab(
          `/${client}/document/${documentDetails.document_handlers[0]?.id}`,
        );
      }
      trackSegment('selectDocument', {
        name: documentDetails.id,
      });
    }
  };

  const currentSelectedIndex = useMemo(() => {
    if (documentDetails) {
      return data?.results.indexOf(documentDetails);
    }
    return undefined;
  }, [data, documentDetails]);

  const prev = () => {
    currentSelectedIndex !== undefined &&
      setDocumentDetails(data?.results[currentSelectedIndex - 1]);
  };

  const next = () => {
    currentSelectedIndex !== undefined &&
      setDocumentDetails(data?.results[currentSelectedIndex + 1]);
  };

  const getPanelActions = (
    list: Array<SearchDocumentItem>,
    item: SearchDocumentItem,
  ) => {
    const isFirst = currentSelectedIndex === 0;
    const isLast = currentSelectedIndex === list.length - 1;

    const actions: Action[] = [
      {
        icon: 'open-in-new',
        label: intl.formatMessage({
          id: 'SearchV3Results.openDocumentLabel',
          defaultMessage: 'Open Document',
          description: 'Label for open document action',
        }),
        value: null,
        onClick: onOpenDocumentClick,
      },
      {
        icon: 'download',
        label: intl.formatMessage({
          id: 'SearchV3Results.downloadLabel',
          defaultMessage: 'Download',
          description: 'Label for download action',
        }),
        value: null,
        onClick: () => {
          saveOriginalDocument(item);
          trackSegment('selectDownloadDocument', {
            name: item.id,
          });
        },
      },
      {
        disabled: isFirst,
        value: null,
        icon: 'chevron-left',
        label: intl.formatMessage({
          id: 'SearchV3Results.previousLabel',
          defaultMessage: 'Previous',
          description: 'Label for previous navigation action',
        }),
        onClick: () => {
          prev();
          trackSegment('selectPreviousDocument', {
            name: item.id,
          });
        },
      },
      {
        disabled: isLast,
        icon: 'chevron-right',
        label: intl.formatMessage({
          id: 'SearchV3Results.nextLabel',
          defaultMessage: 'Next',
          description: 'Label for next navigation action',
        }),
        value: null,
        onClick: () => {
          next();
          trackSegment('selectNextDocument', {
            name: item.id,
          });
        },
      },
    ];
    return actions;
  };

  /*
   * Navigation
   */

  const [handleNavigate] = useSearchNavigation({ sortBy, query });
  const [
    setDocumentSelected,
    renderMultipleLocationModal,
    setOpenDocumentInNewTab,
  ] = useDocumentMultipleLocationsModal(
    getHandlersKeyFromSearchResult,
    false,
    handleNavigate,
  );

  /*
   * Navigation End
   */

  /*
   * Columns
   */

  const hardcodedColumns = [
    {
      key: 'name',
      title: intl.formatMessage({
        id: 'SearchV3Results.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Title for name column in search results table',
      }),
      minWidth: 'm',
      renderCell: (document: SearchDocumentItem) => {
        const handlers = document.document_handlers;
        return (
          <HandlerName
            onNavigate={handleNavigate}
            handlers={handlers as any}
            onClick={() => {
              //@ts-ignore callback type not supported in js hook
              setDocumentSelected(document);
              //@ts-ignore callback type not supported in js hook
              setOpenDocumentInNewTab(false);
            }}
          />
        );
      },
    },
  ];

  const searchFilterColumns = useMemo(
    () =>
      Object.values(searchFilters).filter(
        (searchFilters) => searchFilters.is_requestable_column,
      ),
    [searchFilters],
  );

  const columns = useSearchColumns({
    columnWidths,
    hardcodedColumns,
    searchFilterColumns,
  });

  const sectionsResult = api.endpoints.getFilterSections.useQuery(undefined);

  const { data: filterSections, isSuccess: isSuccessFilterSections } =
    sectionsResult;

  const columnsMap = groupBy(columns, 'section');

  const columnGroups = useMemo(() => {
    if (filterSections?.fieldGroups) {
      return filterSections?.fieldGroups
        .map((group) => ({
          name: group.id,
          label: group.label,
          columns: columnsMap[group.id]?.map((c) => c.key),
        }))
        .filter((group) => group.columns?.length);
    }
    return undefined;
  }, [columnsMap, filterSections]);

  useEffect(() => {
    if (isSuccessFilterSections && currentTableView) {
      const hardcodedColumnKeys = hardcodedColumns.map((col) => col.key);
      const newColumnOrderFromBE = currentTableView.map((col) =>
        String(col.field_id),
      );
      onSetColumnOrder(uniq([...hardcodedColumnKeys, ...newColumnOrderFromBE]));
    }
  }, [currentTableView, isSuccessFilterSections]);

  /*
   * Columns End
   */
  const placeholderContent =
    data && !data.results.length
      ? {
          title: intl.formatMessage({
            id: 'SearchV3Results.noDocumentsFoundTitle',
            defaultMessage: 'No documents found',
            description: 'Title shown when no search results are found',
          }),
        }
      : undefined;

  const selectedFieldIds = useMemo(
    () => columnOrder.filter((col) => col !== 'name').map(String),
    [columnOrder],
  );

  return (
    <Layout direction="column" spacing={8}>
      <ContentContainer placeholderContent={placeholderContent}>
        <>
          <PersistedTable
            context={TableContextType.SearchV3}
            name="search result"
            activeRowId={documentDetails?.id}
            totalCount={getMaximumByLimit(
              data?.meta?.total,
              SEARCH_RESULTS_LIMIT,
            )}
            columns={columns}
            columnGroups={columnGroups}
            data={data?.results ?? []}
            actions={actions}
            isLoading={isLoading}
            state={{
              columnOrder: columnOrder,
              pageIndex: page,
              pageSize,
              sortBy: [sortBy],
              isAllRowsSelected: isAllSelected,
              selectedRowIds: selectedIds,
            }}
            getBulkActions={getBulkActions}
            getRowActions={getRowActions}
            onPaginate={onPaginate}
            onPageSizeChange={onPageSizeChange}
            onSetColumnOrder={onSetColumnOrder}
            onUpdate={handleUpdate}
            onLoadView={onLoadView}
            onSaveView={onSaveView}
            rowDetails={{
              onClick: showDetailsPanel,
              condition: (documentDetails: SearchDocumentItem) =>
                documentDetails?.document_handlers,
            }}
            options={{
              enableExportXlsx: false,
              enablePageSizeSelect: true,
              enableSelectRows: true,
              enableStickyHeader: true,
              stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
            }}
            reactTableOptions={{
              autoResetSortBy: false,
              disableSortRemove: true,
              manualSortBy: true,
            }}
          />
          {renderMultipleLocationModal(null)}
        </>
      </ContentContainer>

      {isUnifiedDetailsPanelEnabled ? (
        <DocumentDetailsPanel
          docHandlerId={(documentDetails?.document_handlers ?? []).map(
            (handler) => handler.id,
          )}
          searchKeyworkMatches={documentDetails?.full_text_matches}
          onHide={hideDetailsPanel}
          featuredClauses={featuredClauses}
          actionsConfig={{
            navigate: {
              onClick: onOpenDocumentClick,
            },
            download: {
              disabled:
                documentDetails &&
                !canDownloadSearchResultItem(documentDetails),
            },
            next: {
              onClick: next,
              disabled:
                data && currentSelectedIndex === data.results.length - 1,
            },
            previous: {
              onClick: prev,
              disabled: currentSelectedIndex === 0,
            },
            delete: {
              onSuccess: refreshSearch,
            },
          }}
        />
      ) : (
        <DetailsPanel
          document={documentDetails}
          onHide={hideDetailsPanel}
          actions={getPanelActions(data?.results || [], documentDetails!)}
        />
      )}
      <ExportExcel
        query={query}
        isVisible={showExportExcel}
        onHide={() => setShowExportExcel(false)}
        totalDocs={data?.meta?.total}
        totalDupsAndDocs={data?.meta?.total_docs_including_dups}
        selectedFieldIds={selectedFieldIds}
      />
      {addToGroupPanel}
    </Layout>
  );
};
